
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { register as registerService } from '../../services/authService';
import { useTranslation } from 'react-i18next';
import styles from './Register.module.css';
import MenelCaptcha from '../common/MenelCaptcha';
import { FaUser, FaEnvelope, FaLock, FaKey, FaEye, FaEyeSlash, FaInfoCircle, FaSignInAlt } from 'react-icons/fa';

const USER_RE = /^[a-zA-Z0-9](?:[a-zA-Z0-9._-]{1,10})[a-zA-Z0-9]$/; // 3–12 total, no empezar/terminar con ._-
const EMAIL_RE = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const UUID_RE  = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const Register = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // form
  const [username, setUsername] = useState('');
  const [nick, setNick] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [referrerCode, setReferrerCode] = useState(''); // UI
  const [showPassword, setShowPassword] = useState(false);

  // ui
  const [passwordStrength, setPasswordStrength] = useState('');
  const [showReferralInfo, setShowReferralInfo] = useState(false);
  const [error, setError] = useState('');             // error general (arriba)
  const [fieldErrors, setFieldErrors] = useState({}); // errores por campo
  const [success, setSuccess] = useState('');
  const [busy, setBusy] = useState(false);

  // captcha (igual que Login)
  const [captchaToken, setCaptchaToken] = useState('');
  const [captchaInput, setCaptchaInput] = useState('');
  const [captchaNonce, setCaptchaNonce] = useState(0);

  // referrer de la URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get('referrerId') || params.get('referralCode'); // acepta ambos
    if (ref) setReferrerCode(ref);
  }, []);

  const clearAllErrors = () => {
    setError('');
    setFieldErrors({});
    setSuccess('');
  };

  const generatePassword = () => {
    const lower = 'abcdefghijklmnopqrstuvwxyz';
    const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    const symbols = '!@#$%^&*()_+~<>?';
    const all = lower + upper + numbers + symbols;
    const len = 12;
    let pwd = '';
    pwd += lower[Math.floor(Math.random()*lower.length)];
    pwd += upper[Math.floor(Math.random()*upper.length)];
    pwd += numbers[Math.floor(Math.random()*numbers.length)];
    pwd += symbols[Math.floor(Math.random()*symbols.length)];
    for (let i = 4; i < len; i++) pwd += all[Math.floor(Math.random()*all.length)];
    setPassword(pwd);
    setConfirmPassword(pwd);
    evaluatePasswordStrength(pwd);
  };

  const evaluatePasswordStrength = (pwd) => {
    const hasLower = /[a-z]/.test(pwd);
    const hasUpper = /[A-Z]/.test(pwd);
    const hasNumber = /[0-9]/.test(pwd);
    const hasSymbol = /[^a-zA-Z0-9]/.test(pwd);
    const long = pwd.length >= 8;
    if (long && hasLower && hasUpper && hasNumber && hasSymbol) setPasswordStrength(t('password_strength_strong'));
    else if (long && (hasLower || hasUpper) && hasNumber) setPasswordStrength(t('password_strength_moderate'));
    else setPasswordStrength(t('password_strength_weak'));
  };

  const onPasswordChange = (e) => {
    const v = e.target.value;
    setPassword(v);
    evaluatePasswordStrength(v);
    if (fieldErrors.password) setFieldErrors(prev => ({ ...prev, password: undefined }));
  };

  const localValidate = () => {
    const fe = {};

    // username y nick: 3–12 y regex como en el server
    if (username.trim().length < 3 || username.trim().length > 12 || !USER_RE.test(username.trim())) {
      fe.username = t('error_username_length') || 'The username must be 3–12 chars, no starting/ending with ._-';
    }
    if (nick.trim().length < 3 || nick.trim().length > 12 || !USER_RE.test(nick.trim())) {
      fe.nick = t('error_nick_length') || 'The nickname must be 3–12 chars, no starting/ending with ._-';
    }

    // email
    if (!EMAIL_RE.test(email.trim())) {
      fe.email = t('error_invalid_email') || 'Email is not valid.';
    }

    // password como el backend
    if (password.length < 8) {
      fe.password = (fe.password ? fe.password + ' • ' : '') + (t('error_password_length_8') || 'Password must be at least 8 characters long.');
    }
    if (!/[A-Z]/.test(password)) {
      fe.password = (fe.password ? fe.password + ' • ' : '') + (t('error_password_upper') || 'Password must contain at least one uppercase letter.');
    }
    if (!/[a-z]/.test(password)) {
      fe.password = (fe.password ? fe.password + ' • ' : '') + (t('error_password_lower') || 'Password must contain at least one lowercase letter.');
    }
    if (!/[0-9]/.test(password)) {
      fe.password = (fe.password ? fe.password + ' • ' : '') + (t('error_password_number') || 'Password must contain at least one number.');
    }
    if (!/[\W]/.test(password)) {
      fe.password = (fe.password ? fe.password + ' • ' : '') + (t('error_password_symbol') || 'Password must contain at least one special character.');
    }

    // confirm
    if (password !== confirmPassword) {
      fe.confirmPassword = t('error_password_mismatch') || 'Passwords do not match.';
    }

    // referralCode (opcional) formato UUID como server
    if (referrerCode && !UUID_RE.test(referrerCode.trim())) {
      fe.referralCode = t('error_invalid_referral_format') || 'The referral code must be a valid UUID.';
    }

    // captcha
    if (!captchaToken || !captchaInput.trim()) {
      fe.captcha = t('error_captcha_required') || 'Please complete the captcha.';
    }

    setFieldErrors(fe);
    setError(Object.keys(fe).length ? (t('fix_the_errors_below') || 'Please fix the errors below.') : '');
    return Object.keys(fe).length === 0;
  };

  const canSubmit =
    !busy &&
    username.trim() && nick.trim() && email.trim() &&
    password && confirmPassword &&
    captchaToken && captchaInput.trim().length >= 4;

  // Normaliza errores del servidor: { errors:[{param,msg}]} o {message} o {error}
  const normalizeServerError = (err) => {
    const data = err?.response?.data;
    const fe = {};
    if (data?.errors && Array.isArray(data.errors) && data.errors.length) {
      for (const e of data.errors) {
        const key = e.param || e.path;
        if (!key) continue;
        fe[key] = fe[key] ? `${fe[key]} • ${e.msg}` : e.msg;
      }
      return { message: t('fix_the_errors_below') || 'Please fix the errors below.', fieldErrors: fe };
    }
    const raw = data?.message || data?.error || err?.message || 'Registration failed';
    return { message: raw, fieldErrors: {} };
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    clearAllErrors();
    if (!localValidate()) return;

    setBusy(true);
    try {
      await registerService(
        username.trim(),
        nick.trim(),
        email.trim(),
        password,
        referrerCode.trim(), // UI -> se mapea a referralCode en el service
        { captchaToken, captchaInput: captchaInput.trim() }
      );
      setSuccess(t('success_registration'));
      setTimeout(() => navigate('/login'), 1200);
    } catch (err) {
      const { message, fieldErrors: fe } = normalizeServerError(err);
      setError(message || '');
      if (fe && Object.keys(fe).length) setFieldErrors(prev => ({ ...prev, ...fe }));

      const m = message || '';
      if (/captcha/i.test(m)) {
        setCaptchaToken(''); setCaptchaInput(''); setCaptchaNonce(n => n + 1);
      }
    } finally {
      setBusy(false);
    }
  };

  const strengthWidth =
    passwordStrength === t('password_strength_strong') ? '100%' :
    passwordStrength === t('password_strength_moderate') ? '60%' : '30%';

  const strengthTone =
    passwordStrength === t('password_strength_strong') ? styles.strOk :
    passwordStrength === t('password_strength_moderate') ? styles.strWarn : styles.strBad;

  // Helpers para marcar inválidos y mostrar mensaje
  const invalidCls = (name) => fieldErrors[name] ? styles.inputInvalid : '';
  const errText = (name, id) =>
    fieldErrors[name] ? <span className={styles.errorText} id={id}>{fieldErrors[name]}</span> : null;

  return (
    <div className={styles.shell}>
      <div className={styles.card}>
        <div className={styles.logoWrap}>
          <img src="/images/logo.webp" alt="Menelik" className={styles.logo} />
        </div>

        <h1 className={styles.title}>{t('register_to_wallet')}</h1>
        <p className={styles.subtitle}>{t('create_your_account')}</p>

        <form className={styles.form} onSubmit={handleRegister} noValidate>
          {/* Alertas generales */}
          {error && <div className={`${styles.alert} ${styles.alertDanger}`} role="alert">{error}</div>}
          {success && <div className={`${styles.alert} ${styles.alertSuccess}`} role="status">{success}</div>}

          {/* Username */}
          <div className={styles.group}>
            <label htmlFor="username" className={styles.label}>{t('username')}</label>
            <div className={styles.withIcon}>
              <span className={styles.leftIcon}><FaUser /></span>
              <input
                id="username"
                className={`${styles.input} ${invalidCls('username')}`}
                value={username}
                onChange={(e) => { setUsername(e.target.value); if (fieldErrors.username) setFieldErrors(p=>({...p, username: undefined})); }}
                placeholder={t('enter_username')}
                autoComplete="username"
                disabled={busy}
                required
                aria-invalid={!!fieldErrors.username}
                aria-describedby={fieldErrors.username ? 'err-username' : undefined}
              />
            </div>
            {errText('username','err-username')}
          </div>

          {/* Nick */}
          <div className={styles.group}>
            <label htmlFor="nick" className={styles.label}>{t('nick')}</label>
            <div className={styles.withIcon}>
              <span className={styles.leftIcon}><FaUser /></span>
              <input
                id="nick"
                className={`${styles.input} ${invalidCls('nick')}`}
                value={nick}
                onChange={(e) => { setNick(e.target.value); if (fieldErrors.nick) setFieldErrors(p=>({...p, nick: undefined})); }}
                placeholder={t('enter_nick')}
                disabled={busy}
                required
                aria-invalid={!!fieldErrors.nick}
                aria-describedby={fieldErrors.nick ? 'err-nick' : undefined}
              />
            </div>
            {errText('nick','err-nick')}
          </div>

          {/* Email */}
          <div className={styles.group}>
            <label htmlFor="email" className={styles.label}>{t('email')}</label>
            <div className={styles.withIcon}>
              <span className={styles.leftIcon}><FaEnvelope /></span>
              <input
                id="email" type="email"
                className={`${styles.input} ${invalidCls('email')}`}
                value={email}
                onChange={(e) => { setEmail(e.target.value); if (fieldErrors.email) setFieldErrors(p=>({...p, email: undefined})); }}
                placeholder={t('enter_email')}
                autoComplete="email"
                disabled={busy}
                required
                aria-invalid={!!fieldErrors.email}
                aria-describedby={fieldErrors.email ? 'err-email' : undefined}
              />
            </div>
            {errText('email','err-email')}
          </div>

          {/* Password */}
          <div className={styles.group}>
            <label htmlFor="password" className={styles.label}>{t('password')}</label>
            <div className={styles.withIcon}>
              <span className={styles.leftIcon}><FaLock /></span>
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                className={`${styles.input} ${invalidCls('password')}`}
                value={password}
                onChange={onPasswordChange}
                placeholder={t('create_password')}
                autoComplete="new-password"
                disabled={busy}
                required
                aria-invalid={!!fieldErrors.password}
                aria-describedby={fieldErrors.password ? 'err-password' : undefined}
              />
              <div className={styles.rightBtns}>
                <button type="button" className={styles.smallBtn} onClick={generatePassword} title={t('generate_password')} tabIndex={-1}><FaKey /></button>
                <button type="button" className={styles.smallBtn} onClick={() => setShowPassword(s => !s)}
                  title={showPassword ? t('hide_password') : t('show_password')} tabIndex={-1}>
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
            </div>
            {password && (
              <div className={styles.strengthWrap} aria-live="polite">
                <div className={`${styles.strengthBar} ${strengthTone}`} style={{ width: strengthWidth }} />
                <span className={styles.strengthLabel}>{passwordStrength}</span>
              </div>
            )}
            {errText('password','err-password')}
          </div>

          {/* Confirm Password */}
          <div className={styles.group}>
            <label htmlFor="confirmPassword" className={styles.label}>{t('confirm_password')}</label>
            <div className={styles.withIcon}>
              <span className={styles.leftIcon}><FaLock /></span>
              <input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                className={`${styles.input} ${invalidCls('confirmPassword')}`}
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); if (fieldErrors.confirmPassword) setFieldErrors(p=>({...p, confirmPassword: undefined})); }}
                placeholder={t('confirm_your_password')}
                autoComplete="new-password"
                disabled={busy}
                required
                aria-invalid={!!fieldErrors.confirmPassword}
                aria-describedby={fieldErrors.confirmPassword ? 'err-confirm' : undefined}
              />
            </div>
            {errText('confirmPassword','err-confirm')}
          </div>

          {/* Referral */}
          <div className={styles.group}>
            <label htmlFor="referrerCode" className={styles.label}>{t('referral_code_optional')}</label>
            <div className={styles.withIcon}>
              <span className={styles.leftIcon}><FaInfoCircle /></span>
              <input
                id="referrerCode"
                className={`${styles.input} ${invalidCls('referralCode')}`}
                value={referrerCode}
                onChange={(e) => { setReferrerCode(e.target.value); if (fieldErrors.referralCode) setFieldErrors(p=>({...p, referralCode: undefined})); }}
                placeholder={t('enter_referral_code')}
                disabled={busy}
                aria-invalid={!!fieldErrors.referralCode}
                aria-describedby={fieldErrors.referralCode ? 'err-ref' : undefined}
              />
              <button type="button" className={styles.smallBtn}
                onClick={() => setShowReferralInfo(s => !s)}
                aria-expanded={showReferralInfo} aria-controls="ref-info" title={t('learn_more_referral')} tabIndex={-1}>
                <FaInfoCircle />
              </button>
            </div>
            {errText('referralCode','err-ref')}

            {showReferralInfo && (
              <div id="ref-info" className={styles.infoBox}>
                <p><strong>{t('referral_info.title')}</strong></p>
                <p>{t('referral_info.description')}</p>
                <ul className={styles.infoList}>
                  <li>{t('referral_info.levels.level1')}</li>
                  <li>{t('referral_info.levels.level2')}</li>
                  <li>{t('referral_info.levels.level3')}</li>
                  <li>{t('referral_info.levels.level4')}</li>
                  <li>{t('referral_info.levels.level5')}</li>
                </ul>
                <p className={styles.infoNote}>{t('referral_info.note')}</p>
              </div>
            )}
          </div>

          {/* CAPTCHA */}
          <div className={styles.group}>
            <MenelCaptcha
              key={captchaNonce}
              onVerify={(token, value) => {
                setCaptchaToken(token || '');
                setCaptchaInput((value || '').trim());
                if (fieldErrors.captcha) setFieldErrors(prev => ({ ...prev, captcha: undefined }));
                if (error && /captcha/i.test(error) && value) setError('');
              }}
            />
            {errText('captcha','err-captcha')}
          </div>

          <button className={styles.submit} type="submit" disabled={!canSubmit}>
            {busy ? <span className={styles.spinner} aria-hidden /> : (<><FaSignInAlt /> {t('register_button')}</>)}
          </button>

          <div className={styles.footerLinks}>
            <button type="button" onClick={() => navigate('/securitynotice')} className={styles.linkBtn}>
              {t('conditions_link')}
            </button>
            <span className={styles.sep}>•</span>
            <button type="button" onClick={() => navigate('/login')} className={styles.linkBtn}>
              {t('go_to_login')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Register;
